import {
    EngageAccount,
    EngageApplication,
    EngageAggregationResponse,
    EngageFilter,
    EngagePage,
    EngageReportTrigger,
    EngageSegment,
    EngageTab,
    EngageTimeSeries,
} from '@/types/engage.types';
// eslint-disable-next-line no-restricted-imports
import store from '@/vuex/store';
import { AggRequest, OptionalRecord } from '@pendo/aggregations';
import { http } from '@pendo/http';
import { AxiosResponse } from 'axios';
import invoke from 'lodash/invoke';
import { migrateLegacyAppIdToArray } from '@/apps/apps.utils';
import { isMobileApp, getAppsForPlatforms } from '@/utils/apps';
import { DashboardWidgetType } from '@/types/dashboards/dashboard.types';

export const reportTriggers: OptionalRecord<EngageReportTrigger, string> = {
    DataExplorer: 'DataExplorer',
    DashboardWidgetLoad: 'DashboardWidgetLoad',
};

const reportTriggerLabel: OptionalRecord<EngageReportTrigger, string> = {
    AiAgentReport: 'AI Agent Report',
    Dashboard: 'Dashboard',
    Workflow: 'Workflow',
    CompletedJourney: 'Completed Journey',
    PortfolioSummary: 'Portfolio Summary',
    DashboardWidgetLoad: 'Dashboard Widget - Load',
    Pages: 'Page List',
    PageDetails: 'Page Detail',
    Features: 'Feature List',
    FeatureDetails: 'Feature Detail',
    TrackEvents: 'Track Event List',
    TrackEventsDetails: 'Track Event Detail',
    ProductAreas: 'Product Area List',
    ProductAreaDetails: 'Product Area Detail',
    Visitors: 'Visitor Overview',
    VisitorsChart: 'Visitor Overview Chart',
    VisitorList: 'Visitor Report',
    VisitorDetails: 'Visitor Detail',
    VisitorDetailsOverviewChart: 'Visitor Details Overview Chart',
    VisitorDetailFeedback: 'Visitor Detail Feedback',
    Accounts: 'Account Overview',
    AccountsOverviewChart: 'Account Overview Chart',
    AccountList: 'Account Report',
    AccountDetails: 'Account Detail',
    AccountDetailsOverviewChart: 'Account Details Overview Chart',
    AccountDetailFeedback: 'Account Detail Feedback',
    ParentAccountOverview: 'Parent Account Overview',
    ParentAccountOverviewChart: 'Parent Account Overview Chart',
    ParentAccountList: 'Parent Account Report',
    ParentAccount: 'Parent Account Detail',
    ParentAccountDetailsOverviewChart: 'Parent Account Details Overview Chart',
    ParentAccountDetailFeedback: 'Parent Account Detail Feedback',
    DataExplorer: 'Data Explorer',
    Retention: 'Retention Report',
    Path: 'Path Report',
    Funnel: 'Funnel Report',
    ProductEngagementScore: 'PES',
    PesAdoption: 'PES - Adoption',
    PesGrowth: 'PES - Growth',
    PesStickiness: 'PES - Stickiness',
};

const reportTriggerTab: OptionalRecord<EngageReportTrigger, EngageTab> = {
    Dashboard: 'Dashboards',
    PortfolioSummary: 'Dashboards',
    DashboardWidgetLoad: 'Dashboards',
    AiAgentReport: 'Product',
    Pages: 'Product',
    PageDetails: 'Product',
    Features: 'Product',
    FeatureDetails: 'Product',
    TrackEvents: 'Product',
    TrackEventsDetails: 'Product',
    ProductAreas: 'Product',
    ProductAreaDetails: 'Product',
    Visitors: 'People',
    VisitorList: 'People',
    VisitorDetails: 'People',
    VisitorDetailsOverviewChart: 'People',
    VisitorsChart: 'People',
    VisitorDetailFeedback: 'People',
    Accounts: 'People',
    AccountList: 'People',
    AccountDetails: 'People',
    AccountDetailsOverviewChart: 'People',
    AccountsOverviewChart: 'People',
    AccountDetailFeedback: 'People',
    ParentAccountOverview: 'People',
    ParentAccountList: 'People',
    ParentAccount: 'People',
    ParentAccountDetailFeedback: 'People',
    ParentAccountDetailsOverviewChart: 'People',
    ParentAccountOverviewChart: 'People',
    // The 'Behavior' tab was renamed to 'Analytics' in the FE, but track events continue to use 'Behavior' for consistency with historical data.
    DataExplorer: 'Behavior',
    Retention: 'Behavior',
    Path: 'Behavior',
    Funnel: 'Behavior',
    ProductEngagementScore: 'Behavior',
    PesAdoption: 'Behavior',
    PesGrowth: 'Behavior',
    PesStickiness: 'Behavior',
    Workflow: 'Behavior',
    CompletedJourney: 'Behavior',
};

interface TrackReportDefinition {
    eventProperties?: (
        aggRequest: AggRequest,
        aggResponse: EngageAggregationResponse,
    ) => Record<string, string | number | boolean>;
    filters?: (aggRequest: AggRequest, aggResponse: EngageAggregationResponse) => EngageFilter[];
    reportTrigger?: (aggRequest: AggRequest, aggResponse: EngageAggregationResponse) => EngageReportTrigger | null;
    widgetType?: DashboardWidgetType;
}

interface SelectAppsDefinition {
    totalApps: number;
    webApps: number;
    mobileApps: number;
}

// Dashboard has two different route names. Please keep this list alphabetical. (Structure: Aggregation Name => Report Definition)
const dashboardReportDefinitions: Record<string, TrackReportDefinition> = {
    'ActiveHourlyVisitorsChart': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'activeVisitorsHourlyChart',
    },
    'app-version-chart': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'appVersion',
    },
    'AppUsageWidget': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'appUsage',
    },
    'BrowserUsageWidget': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'browserUsageChart',
    },
    'dashboard-nps-donut': {
        reportTrigger: (aggRequest: AggRequest) => {
            return aggRequest.requests[0].params?.runTrends ? 'DashboardWidgetLoad' : null;
        },
        widgetType: 'npsWidget',
    },
    'dashboard-nps-responses': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'npsWidget',
    },
    'device-usage-chart': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'deviceUsage',
    },
    'experiment': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'guideExperimentSummary',
    },
    'FeatureAdoptionAggregation': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'featureAdoption',
    },
    'guide-overview-widget': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'guideOverview',
    },
    'MetadataMetrics': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'metadataDetails',
    },
    'most-seen-guides': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'mostSeenGuides',
    },
    'NpsMetadataTableAggregation': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'npsWidget',
    },
    'NpsResponses': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'npsWidget',
    },
    'NpsScatterPlotAggregation': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'npsScatterPlot',
    },
    'NpsTrendsAggregation': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'npsWidget',
    },
    'os-usage-chart': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'osChart',
    },
    'os-version-chart': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'osVersion',
    },
    'poll-bar-graph-v2': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'pollResults',
    },
    'PES-Accounts-By-Active-Visitor-Growth': {
        reportTrigger: () => 'PesGrowth',
        widgetType: 'productEngagementScore',
    },
    'PES-Adoption-Trending': {
        reportTrigger: () => 'PesAdoption',
        widgetType: 'productEngagementScore',
    },
    'prioritizeIdeas': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'prioritizeIdeas',
    },
    'Product-Engagement-Score': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'productEngagementScore',
    },
    'ProductRequestsByGroupAggregation': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'productRequestsByGroup',
    },
    'ProductRequestsByStatusAggregation': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'productRequestsByStatus',
    },
    'RecentProductRequestsAggregation': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'recentProductRequests',
    },
    'retention-by-usage-cohorts': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'retentionByUsage',
    },
    'SavedViewsCountOverTimeAggregation': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'savedViewsCountOverTime',
    },
    'SavedViewsProductAreaAggregation': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'savedViewsProductArea',
    },
    'SavedViewsStatusAggregation': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'savedViewsStatus',
    },
    'SavedViewsTopAccountsAggregation': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'savedViewsTopAccounts',
    },
    'scatter-chart-feature': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'scatterChart',
    },
    'scatter-chart-page': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'scatterChart',
    },
    'scatter-chart-trackType': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'scatterChart',
    },
    'tag-overview-feature': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'tagOverview',
    },
    'tag-overview-page': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'tagOverview',
    },
    'User-Frequency-Widget': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'dwmChart',
    },
    'VisitorOverview-last30days': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'visitorOverview',
    },
    'VocItemsAggregation': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'savedViewsSummary',
    },
    'ValidateTopIdeasAggregation': {
        reportTrigger: () => 'DashboardWidgetLoad',
        widgetType: 'voteCount',
    },
};

// The full look up table for routes and aggregations to their respective report definition. Please keep this table alphabetical. (Structure: Route Name => Aggregation Name => Report Definition)
const reportDefinitionLookupTable: OptionalRecord<EngagePage, Record<string, TrackReportDefinition>> = {
    Dashboard: dashboardReportDefinitions,
    DefaultDashboard: dashboardReportDefinitions,
    Features: {
        PageFeatureAlertsAggregationCount: {
            filters: () => ['account', 'app', 'dateRange', 'segment'],
            reportTrigger: () => 'Features',
        },
    },
    Funnel: {
        funnel: {
            reportTrigger: () => 'Funnel',
        },
    },
    Pages: {
        PageFeatureAlertsAggregationCount: {
            filters: () => ['account', 'app', 'dateRange', 'segment'],
            reportTrigger: () => 'Pages',
        },
    },
    Path: {
        path: {
            reportTrigger: () => 'Path',
        },
    },
    PortfolioSummary: {
        'portfolio-attach-rate': {
            filters: () => ['app', 'dateRange', 'segment'],
            reportTrigger: () => 'PortfolioSummary',
        },
    },
    ProductAreas: {
        GroupsListAggregation: {
            filters: () => ['account', 'app', 'dateRange', 'segment'],
            reportTrigger: () => 'ProductAreas',
        },
    },
    ProductAreaDetails: {
        GroupPageAggregation: {
            filters: () => ['account', 'app', 'dateRange', 'segment'],
            reportTrigger: (aggRequest: AggRequest) => {
                return aggRequest.requests[0].params?.kindEventsSource === 'pageEvents' ? 'ProductAreaDetails' : null;
            },
        },
    },
    ProductEngagementScore: {
        'PES-Accounts-By-Active-Visitor-Growth': {
            filters: () => ['account', 'app', 'dateRange', 'segment'],
            reportTrigger: () => 'PesGrowth',
        },
        'PES-Adoption-Trending': {
            filters: () => ['account', 'app', 'dateRange', 'segment'],
            reportTrigger: () => 'PesAdoption',
        },
        'Product-Engagement-Score': {
            filters: () => ['account', 'app', 'dateRange', 'segment'],
            reportTrigger: () => 'ProductEngagementScore',
        },
    },
    Retention: {
        'cohort-retention-summary': {
            reportTrigger: () => 'Retention',
        },
        'countable-retention-summary': {
            reportTrigger: () => 'Retention',
        },
    },
    TrackEvents: {
        TrackEventList: {
            filters: () => ['account', 'app', 'dateRange', 'segment'],
            reportTrigger: () => 'TrackEvents',
        },
    },
};

function accountEventPropertyValue (account: EngageAccount): string {
    if (!account) return '';

    return account.id === '' ? 'All Accounts' : 'Single Account';
}

function appTypeEventPropertyValue (app: EngageApplication | EngageApplication[]): string {
    // some reports will have appId as number, string etc. that needs to be converted to array to support multiple apps
    // this logic can be updated or removed once all reports are migrated
    const appArray = migrateLegacyAppIdToArray(app);
    if (!app || appArray.length === 0) {
        return '';
    }

    const appId = appArray[0].id;
    const hasWeb = appArray.find((app) => app.platform === 'web') ? 'Web' : null;
    const hasExtension = appArray.find((app) => app.platform === 'extension') ? 'Extension' : null;
    const hasMobile = appArray.find((app) => isMobileApp(app.platform)) ? 'Mobile' : null;
    const appTypes = [hasWeb, hasExtension, hasMobile].filter((val) => val !== null);
    const appTypesString = appTypes.join(', ');

    switch (appId) {
        case '*':
            return 'All Apps';

        case 'web':
            return 'All Web';

        case 'extension':
            return 'All Extension';

        case 'mobile':
            return 'All Mobile';

        default:
            return appTypesString;
    }
}

function selectedAppsEventPropertyValue (app: EngageApplication | EngageApplication[]): SelectAppsDefinition {
    // This instance won't be temporary unless we migrate all report objects.
    // Some reports will have a number, string etc. that needs to be converted.
    const appArray = migrateLegacyAppIdToArray(app);
    if (!app || appArray.length === 0) {
        return {
            totalApps: 0,
            webApps: 0,
            mobileApps: 0,
        };
    }
    const appId = appArray[0].id;
    const totalAppsCount = store.getters['apps/list']?.length;
    const totalWebAppsCount = getAppsForPlatforms(store.getters['apps/list'], 'web')?.length;
    const totalMobileAppsCount = getAppsForPlatforms(store.getters['apps/list'], ['ios', 'android'])?.length;

    switch (appId) {
        case '*':
            return {
                totalApps: totalAppsCount,
                webApps: totalWebAppsCount,
                mobileApps: totalMobileAppsCount,
            };
        case 'web':
            return {
                totalApps: totalWebAppsCount,
                webApps: totalWebAppsCount,
                mobileApps: 0,
            };
        case 'mobile':
            return {
                totalApps: totalMobileAppsCount,
                webApps: 0,
                mobileApps: totalMobileAppsCount,
            };
        default:
            return {
                totalApps: appArray.length,
                webApps: appArray.filter((app) => app.platform === 'web').length,
                mobileApps: appArray.filter((app) => isMobileApp(app.platform)).length,
            };
    }
}

function dateRangeEventPropertyValue (timeSeries: EngageTimeSeries): string {
    if (!timeSeries) return '';

    switch (timeSeries.config.type) {
        case 'Today':
        case 'Yesterday':
        case 'Last 7 Days':
        case 'Last 30 Days':
        case 'Last 90 Days':
        case 'Last 180 Days':
            return timeSeries.config.type;

        default:
            return 'Custom';
    }
}

function segmentEventPropertyValue (segment: EngageSegment): string {
    if (!segment) return '';

    const isPendoSegment = segment.default || segment.id.includes('_PENDO_DEFAULT_');

    return isPendoSegment ? 'Pendo' : 'User';
}

function buildGlobalFilterEventProperties (filters: EngageFilter[]): Record<string, string | number> {
    const eventProperties: Record<string, string | number> = {};

    filters.forEach((filterName) => {
        switch (filterName) {
            case 'account':
                {
                    const accountValue = accountEventPropertyValue(
                        store.getters['globalFilters/selectedAccountAccount'],
                    );
                    if (accountValue) eventProperties.account = accountValue;
                }
                break;

            case 'app':
                {
                    const appValue = appTypeEventPropertyValue(store.getters['globalFilters/selectedApp']);
                    const selectedApps = selectedAppsEventPropertyValue(store.getters['globalFilters/selectedApp']);
                    if (appValue) eventProperties.appType = appValue;
                    eventProperties.totalApps = selectedApps.totalApps;
                    eventProperties.webApps = selectedApps.webApps;
                    eventProperties.mobileApps = selectedApps.mobileApps;
                }
                break;

            case 'dateRange':
                {
                    const dateRangeValue = dateRangeEventPropertyValue(store.getters['globalFilters/timeSeries']);
                    if (dateRangeValue) eventProperties.dateRange = dateRangeValue;
                }
                break;

            case 'segment':
                {
                    const segmentValue = segmentEventPropertyValue(store.getters['globalFilters/selectedSegment']);
                    if (segmentValue) eventProperties.segment = segmentValue;
                }
                break;
        }
    });

    return eventProperties;
}

export function trackReport (
    reportTrigger: EngageReportTrigger,
    filters: EngageFilter[] = [],
    additionalEventProperties: Record<string, string | number | boolean> = {},
): void {
    invoke(window, 'pendo.track', 'Ran Report', {
        reportType: reportTriggerLabel[reportTrigger],
        tab: reportTriggerTab[reportTrigger],
        ...buildGlobalFilterEventProperties(filters),
        ...additionalEventProperties,
    });
}

export function trackReportCancellation (
    reportTrigger: EngageReportTrigger,
    filters: EngageFilter[] = [],
    additionalEventProperties: Record<string, string | number | boolean> = {},
): void {
    invoke(window, 'pendo.track', 'Cancelled Report', {
        reportType: reportTriggerLabel[reportTrigger],
        tab: reportTriggerTab[reportTrigger],
        ...buildGlobalFilterEventProperties(filters),
        ...additionalEventProperties,
    });
}

// Be very careful here!
// - This has the possibility of breaking all requests in the application.
// - We want to escape as quickly as possible out of here if we can, this can slow down requests.
// - Any modifications to the response object will be sent down the line.
// - Failure to return the response object will break the original request.
export function responseInterceptor (response: AxiosResponse, routeName?: string) {
    // The Vue Router must have a route
    if (!routeName) return response;

    // Are we tracking any aggregations on the current route?
    const aggLookup = reportDefinitionLookupTable[routeName as EngagePage];
    if (!aggLookup) return response;

    // Make sure our response object has the fields we need.
    if (!response || !response.config || !response.config.data || !response.config.url) return response;

    // We are only interested in OK and ACCEPTED responses.
    if (response.status !== 200 && response.status !== 202) return response;

    // We are only interested in POST requests.
    if (response.config.method !== 'post') return response;

    // We are only interested in requests made to the aggregation endpoint.
    if (!response.config.url.startsWith('/api/aggregation/s/')) return response;

    let aggReqData: AggRequest;
    try {
        aggReqData = JSON.parse(response.config.data) as AggRequest;
        if (!aggReqData) return response;
    } catch {
        return response;
    }

    const reportsTracked: Partial<Record<EngageReportTrigger, boolean>> = {};
    aggReqData.requests.forEach(({ name }) => {
        const trackReportDefinition: TrackReportDefinition = aggLookup[name];

        let reportTrigger: EngageReportTrigger | null;
        if (trackReportDefinition && trackReportDefinition.reportTrigger) {
            reportTrigger = trackReportDefinition.reportTrigger(aggReqData, response.data);
        } else {
            return;
        }

        if (reportTrigger && !reportsTracked[reportTrigger]) {
            try {
                const eventProperties = trackReportDefinition.eventProperties
                    ? trackReportDefinition.eventProperties(aggReqData, response.data)
                    : {};
                if (trackReportDefinition.widgetType) {
                    eventProperties.widgetType = trackReportDefinition.widgetType;
                }
                trackReport(
                    reportTrigger,
                    trackReportDefinition.filters ? trackReportDefinition.filters(aggReqData, response.data) : [],
                    eventProperties,
                );
                reportsTracked[reportTrigger] = true;
            } catch (error) {
                if (process.env.NODE_ENV !== 'production') {
                    // eslint-disable-next-line no-console
                    console.warn(
                        `There was an issue while trying to track a report. (Route: "${routeName}", AggregationName: "${name}")`,
                        error,
                    );
                }
            }
        }
    });

    return response;
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export function initAutoReportTracker (router) {
    http.interceptors.response.use((response) => responseInterceptor(response, router.currentRoute.value.name));
}

export default trackReport;
